import { useMemo } from "react";
import moment from "moment";
import { object, string, number, mixed, array } from "yup";
import type { BooleanSwitch } from "types";
import { REGEXP } from "consts/validation";
import { BooleanSwitchValue } from "consts/general";
import { FormField } from "./types";

const useValidationSchema = () =>
    useMemo(() => (
        object().shape({
            [FormField.LegalNameOfBusiness]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(150)
                .label('Legal Name of Business'),

            [FormField.TradeName]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('Trade Name'),

            [FormField.EntityType]: string()
                .nullable()
                .required()
                .label('Entity Type'),

            [FormField.IncorporationId]: string()
                .nullable()
                .required()
                .trim()
                .min(3)
                .max(50)
                .matches(REGEXP.VentureId, 'Only alphanumeric, hyphens, slashes, and spaces allowed')
                .label('Incorporation ID'),

            [FormField.CountryOfIncorporation]: array()
                .nullable()
                .required('Select at least one country')
                .of(string())
                .min(1, 'Select at least one country')
                .label('Countries of Business Activity'),

            [FormField.IncorporationDate]: mixed()
                .nullable()
                .required()
                .test('is-valid-date', 'Invalid date', (value) => {
                    if (!value) {
                        return false;
                    }

                    return value.isValid();
                })
                .test('not-in-future', 'Incorporation date cannot be in the future', (value) => {
                    if (!value) {
                        return false;
                    }

                    const today = moment().startOf('day');

                    return value
                        .startOf('day')
                        .isSameOrBefore(today);
                })
                .label('Incorporation Date'),

            [FormField.YearsUnderOwnership]: number()
                .nullable()
                .required()
                .integer()
                .min(0)
                .max(200)
                .label('Years Under Current Ownership'),

            [FormField.CompanyAddress]: string()
                .nullable()
                .required()
                .trim()
                .min(5)
                .max(200)
                .label('Company Address'),

            [FormField.MailingAddress]: string()
                .nullable()
                .optional()
                .trim()
                .min(5)
                .max(200)
                .label('Mailing Address'),

            [FormField.Country]: array()
                .nullable()
                .required('Select at least one country')
                .of(string())
                .min(1, 'Select at least one country')
                .label('Countries of Business Activity'),

            [FormField.City]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('City'),

            [FormField.PostalCode]: string()
                .nullable()
                .required()
                .trim()
                .min(3)
                .max(12)
                .matches(REGEXP.PostalCode, 'Enter a valid postal code')
                .label('Postal Code'),

            [FormField.ContactName]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('Contact Name'),

            [FormField.ContactPhone]: string()
                .nullable()
                .required()
                .trim()
                .matches(REGEXP.Phone, 'Enter a valid phone number')
                .max(20)
                .label('Contact Phone'),

            [FormField.ContactEmail]: string()
                .nullable()
                .required()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('Contact Email'),

            [FormField.TechContactName]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('Technical Contact Name'),

            [FormField.TechContactPhone]: string()
                .nullable()
                .required()
                .trim()
                .matches(REGEXP.Phone, 'Enter a valid phone number')
                .max(20)
                .label('Technical Contact Phone'),

            [FormField.TechContactEmail]: string()
                .nullable()
                .required()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('Technical Contact Email'),

            [FormField.FinContactName]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('Financial Contact Name'),

            [FormField.FinContactPhone]: string()
                .nullable()
                .required()
                .trim()
                .matches(REGEXP.Phone, 'Enter a valid phone number')
                .max(20)
                .label('Financial Contact Phone'),

            [FormField.FinContactEmail]: string()
                .nullable()
                .required()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('Financial Contact Email'),

            [FormField.EmergencyContactName]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('Emergency Contact Name'),

            [FormField.EmergencyContactPhone]: string()
                .nullable()
                .required()
                .trim()
                .matches(REGEXP.Phone, 'Enter a valid phone number')
                .max(20)
                .label('Emergency Contact Phone'),

            [FormField.EmergencyContactEmail]: string()
                .nullable()
                .required()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('Emergency Contact Email'),

            [FormField.WishDirectIntegration]: number()
                .nullable()
                .required(),

            [FormField.WishDirectIntegrationInput]: string()
                .nullable()
                .when(FormField.WishDirectIntegration, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.Off),
                    then: validator => validator
                        .required()
                        .trim()
                        .min(2)
                        .max(100)
                        .label('Wish Direct Integration Input')
                }),

            [FormField.IntegrationContactEmail]: string()
                .nullable()
                .required()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('Integration Contact Email')
        })
    ), []);

export default useValidationSchema;
